import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Center,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

import { PaginationState, SortingState } from '@tanstack/react-table';

import { isAfter, isBefore, format, parseISO } from 'date-fns';

import {
  Badge,
  DEFAULT_DATE_FORMAT,
  EarningResponse,
  PayCycle,
  TimesheetEarningsStatus,
} from '@aslanpay/portal-common-lib';

import { Table } from 'app/components/UI/Table/Table';

import ProfileField from 'app/components/UI/ProfileField';
import ProfileSection from 'app/components/UI/ProfileSection';

import { defaultPageSize } from 'app/constants/table';
import { EarningsStatusDetails } from 'app/constants/earnings';

import useFetchEarnings from 'app/hooks/useFetchEarnings';
import useFetchGlobalAllowance from 'app/hooks/useFetchGlobalAllowance';

import getEarningsColumns from 'app/utils/table/earnings';
import { formatCurrency } from 'app/utils/strings';

import EarningDetailDrawer from './EarningDetailDrawer';
import { EarningsTableRow, IndividualEarningsTabProps } from './types';

const PAY_CYCLE_BADGE_STATUS: Record<PayCycle['status'], 'active' | 'created' | 'paused'> = {
  IN_PROGRESS: 'active',
  COMPLETED: 'created',
  PENDING: 'paused',
};

const buildPeriodLabel = (cycle: PayCycle, isMonthly: boolean): string => {
  if (isMonthly) {
    return format(parseISO(cycle.startDate), 'MMMM yyyy');
  }

  return `${format(parseISO(cycle.startDate), DEFAULT_DATE_FORMAT)} - ${format(
    parseISO(cycle.endDate),
    DEFAULT_DATE_FORMAT
  )}`;
};

const compareRows = (a: EarningsTableRow, b: EarningsTableRow, sorting: SortingState) => {
  const sort = sorting[0];
  if (!sort) return 0;

  const aValue = a[sort.id as keyof EarningsTableRow];
  const bValue = b[sort.id as keyof EarningsTableRow];

  let result = 0;
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    result = aValue - bValue;
  } else {
    result = String(aValue ?? '').localeCompare(String(bValue ?? ''));
  }

  return sort.desc ? -result : result;
};

function IndividualEarningsTab({ individual, payCalendar }: IndividualEarningsTabProps) {
  // Pay cycles ordered oldest-to-newest so prev/next steps chronologically.
  const sortedCycles = useMemo(
    () => [...(payCalendar?.payCycles ?? [])].sort((a, b) => a.period - b.period),
    [payCalendar]
  );

  // Default to the cycle containing today, falling back to the in-progress cycle, then the latest.
  const initialCycleIndex = useMemo(() => {
    if (sortedCycles.length === 0) return -1;

    const today = new Date();
    const currentIndex = sortedCycles.findIndex(
      (cycle) =>
        !isBefore(today, parseISO(cycle.startDate)) && !isAfter(today, parseISO(cycle.endDate))
    );
    if (currentIndex !== -1) return currentIndex;

    const inProgressIndex = sortedCycles.findIndex((cycle) => cycle.status === 'IN_PROGRESS');
    if (inProgressIndex !== -1) return inProgressIndex;

    return sortedCycles.length - 1;
  }, [sortedCycles]);

  const [cycleIndex, setCycleIndex] = useState(initialCycleIndex);

  useEffect(() => {
    setCycleIndex(initialCycleIndex);
  }, [initialCycleIndex]);

  const selectedCycle = cycleIndex >= 0 ? sortedCycles[cycleIndex] : undefined;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const { isOpen: isDetailOpen, onOpen: onOpenDetail, onClose: onCloseDetail } = useDisclosure();
  const [selectedEarning, setSelectedEarning] = useState<EarningResponse | null>(null);
  const [selectedKey, setSelectedKey] = useState<string>();

  const handleSelectEarning = (row: EarningsTableRow) => {
    setSelectedEarning(row.earning);
    setSelectedKey(row.key);
    onOpenDetail();
  };

  const handleCloseDetail = () => {
    setSelectedKey(undefined);
    onCloseDetail();
  };

  // Reset to the first page whenever the selected pay cycle changes.
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [selectedCycle?.id]);

  const { data: earnings, isFetching } = useFetchEarnings(
    { individualId: individual?.id, payCycleId: selectedCycle?.id, sort: 'DESC' },
    !!individual?.id && !!selectedCycle?.id
  );

  const isCurrentPayCycle = selectedCycle?.status === 'IN_PROGRESS';
  const { data: globalAllowance, isFetching: isFetchingGlobalAllowance } = useFetchGlobalAllowance(
    individual?.id,
    isCurrentPayCycle
  );

  const rows: EarningsTableRow[] = useMemo(
    () =>
      (earnings ?? []).map((earning, index) => ({
        key: `${earning.payCycleId}-${earning.earningDate ?? earning.date ?? index}-${
          earning.earningsStatus ?? 'NA'
        }-${index}`,
        date: earning.earningDate ?? earning.date,
        grossEarnings: earning.totalGrossEarnings,
        netEarnings: earning.totalNetEarnings,
        status: earning.earningsStatus,
        earning,
      })),
    [earnings]
  );

  const sortedRows = useMemo(() => {
    if (sorting.length === 0) return rows;
    return [...rows].sort((a, b) => compareRows(a, b, sorting));
  }, [rows, sorting]);

  const pageRows = sortedRows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);

  // Only key the statuses actually present so the legend matches what is on screen.
  const statusesPresent = useMemo(() => {
    const present = new Set(rows.map((row) => row.status).filter(Boolean));
    return (Object.keys(EarningsStatusDetails) as TimesheetEarningsStatus[]).filter((status) =>
      present.has(status)
    );
  }, [rows]);

  const isMonthly = payCalendar?.payFrequency === 'MONTHLY';

  if (!payCalendar || sortedCycles.length === 0) {
    return (
      <Center width="full" py="60px">
        <Text textStyle="body" color="brand.grey">
          No pay calendar with pay cycles is configured for this employee.
        </Text>
      </Center>
    );
  }

  return (
    <VStack align="stretch" w="full" spacing="20px">
      <Flex align="center" justify="space-between" w="full">
        <IconButton
          aria-label="Previous pay cycle"
          icon={<ChevronLeftIcon boxSize="24px" />}
          variant="outline"
          size="sm"
          isDisabled={cycleIndex <= 0}
          onClick={() => setCycleIndex((prev) => Math.max(prev - 1, 0))}
        />
        <VStack spacing="4px">
          <Text textStyle="h5SemiBold" color="brand.darkPurple">
            {selectedCycle ? buildPeriodLabel(selectedCycle, isMonthly) : '-'}
          </Text>
          <HStack spacing="8px">
            <Text textStyle="smallRegular" color="brand.grey">
              Period {selectedCycle?.period}
            </Text>
            {selectedCycle?.status && (
              <Badge
                value={selectedCycle.status}
                status={PAY_CYCLE_BADGE_STATUS[selectedCycle.status]}
              />
            )}
            {isFetching && <Spinner size="xs" color="brand.purple" />}
          </HStack>
        </VStack>
        <IconButton
          aria-label="Next pay cycle"
          icon={<ChevronRightIcon boxSize="24px" />}
          variant="outline"
          size="sm"
          isDisabled={cycleIndex >= sortedCycles.length - 1}
          onClick={() => setCycleIndex((prev) => Math.min(prev + 1, sortedCycles.length - 1))}
        />
      </Flex>

      {isCurrentPayCycle && (
        <ProfileSection
          title="Current request allowance"
          tooltip="The live global allowance after active Set & Spend and savings requests."
          maxW="full"
        >
          {isFetchingGlobalAllowance ? (
            <Center py="10px">
              <Spinner size="sm" color="brand.purple" />
            </Center>
          ) : (
            globalAllowance && (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="20px" w="full">
                <ProfileField
                  label="Approved earnings"
                  value={formatCurrency(
                    globalAllowance.totalGrossEarnings,
                    payCalendar.currency,
                    'en-GB'
                  )}
                />
                <ProfileField
                  label={`Global allowance (${globalAllowance.globalAllowancePercentage}%)`}
                  value={formatCurrency(
                    globalAllowance.globalAllowance,
                    payCalendar.currency,
                    'en-GB'
                  )}
                />
                <ProfileField
                  label="Active requests"
                  tooltip="Active Set & Spend and savings requests currently deducted from the global allowance."
                  value={formatCurrency(
                    globalAllowance.totalActiveDeductionsRequestsAmount,
                    payCalendar.currency,
                    'en-GB'
                  )}
                />
                <ProfileField
                  label="Available to request"
                  value={formatCurrency(
                    globalAllowance.availableGlobalAllowance,
                    payCalendar.currency,
                    'en-GB'
                  )}
                  isHighlighted
                />
              </SimpleGrid>
            )
          )}
        </ProfileSection>
      )}

      {statusesPresent.length > 0 && (
        <Box>
          <Text textStyle="smallSemiBold" color="brand.darkPurple" mb="10px">
            Status key
          </Text>
          <VStack align="stretch" spacing="8px">
            {statusesPresent.map((status) => (
              <HStack key={status} spacing="10px" align="center">
                <Box minW="90px">
                  <Badge
                    value={EarningsStatusDetails[status].label}
                    status={EarningsStatusDetails[status].badgeStatus}
                  />
                </Box>
                <Text textStyle="smallRegular" color="brand.grey">
                  {EarningsStatusDetails[status].description}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      <Table
        columns={getEarningsColumns(payCalendar.currency, handleSelectEarning, selectedKey)}
        data={pageRows}
        total={sortedRows.length}
        pageNumber={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
      />

      <EarningDetailDrawer
        earning={selectedEarning}
        currency={payCalendar.currency}
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
      />
    </VStack>
  );
}

export default IndividualEarningsTab;
